import React, { ChangeEvent, FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useToast } from '@thingslog/ui-components';
import TextField from '@mui/material/TextField';
import { KeycloakTokenResponseDto, UserDto } from '@thingslog/repositories';
import JwtValidator from '../../common/JwtValidator';
import { DateUtil } from '../../common/DateUtil';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../reducers';
import { startChangePassword } from '@thingslog/utils';
import { apiTokenQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';

type InfoRowProps = { label: React.ReactNode; value: React.ReactNode };

const InfoRow: FC<InfoRowProps> = (props: InfoRowProps) => {
  const { label, value } = props;
  return (
    <div className="flex flex-row items-start justify-between gap-4">
      <Typography variant="body2" fontWeight={700} color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" className="text-right break-words">
        {value}
      </Typography>
    </div>
  );
};

const UserInfo: FC<UserInfoProps> = ({ userInfo }: UserInfoProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const jwtValidator = useMemo(() => new JwtValidator(), []);
  const { hasRoles } = jwtValidator;
  const isRestrictedAccess = useSelector((state: ReduxState) => state.auth.isRestricted);
  const [clientId, setClientId] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [showClientId, setShowClientId] = useState<boolean>(false);
  const [showClientSecret, setShowClientSecret] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const { useGenerateApiToken } = useMemo(() => apiTokenQueryClient, []);

  const generateApiTokenQuery = useGenerateApiToken({
    onSuccess: (response: KeycloakTokenResponseDto) => {
      // Keycloak reports a failed grant in the body, not the status code.
      if (!response.access_token) {
        toast({ type: 'error', message: t('profile_page_token_generate_error') });
        return;
      }

      setToken(response.access_token);
      setExpiresInSeconds(response.expires_in ?? null);
      toast({ type: 'success', message: t('profile_page_token_generated_success') });
    },
    onError: (error: AxiosError) => {
      toast({ type: 'error', message: t('profile_page_token_generate_error') });
    },
  });

  const validFor = DateUtil.getHumanReadableDurationFromSeconds(expiresInSeconds, t);

  const isGenerating = generateApiTokenQuery.isLoading;

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(token);
      toast({ type: 'success', message: t('profile_page_copied_to_clipboard') });
    } catch {
      toast({ type: 'error', message: t('profile_page_failed_to_copy') });
    }
  };

  const generateToken = (): void => {
    generateApiTokenQuery.mutate({ clientId, clientSecret });
  };

  return (
    <div className="px-4 py-4">
      <Typography variant="h6" fontWeight={700} className="mb-4">
        {t('profile_page_user_info_label')}
      </Typography>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <Paper variant="outlined" className="p-4">
          <div className="flex flex-col gap-4">
            <Typography variant="subtitle1" fontWeight={700}>
              {t('profile_page_user_info_label')}
            </Typography>
            <Divider />

            <div className="flex flex-col gap-2.5">
              <InfoRow
                label={t('profile_page_name')}
                value={`${userInfo?.firstName ?? ''} ${userInfo?.lastName ?? ''}`.trim() || '—'}
              />
              <InfoRow label={t('profile_page_email')} value={userInfo?.email || '—'} />
            </div>

            {!isRestrictedAccess && (
              <>
                <Divider />
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    onClick={(): void => {
                      startChangePassword();
                    }}
                  >
                    {t('profile_page_change_password')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Paper>

        {hasRoles(['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_USER']) && (
          <Paper variant="outlined" className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('profile_page_api_token')}
                </Typography>
                {validFor && (
                  <Typography variant="body2" color="text.secondary">
                    {t('profile_page_api_token_valid_for', { duration: validFor })}
                  </Typography>
                )}
              </div>

              <div className="flex flex-col gap-4 sm:flex-row">
                <TextField
                  value={clientId}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                    setClientId(event.target.value)
                  }
                  type={showClientId ? 'text' : 'password'}
                  size="small"
                  label={t('profile_page_client_id')}
                  fullWidth
                  disabled={isGenerating}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={
                            (showClientId
                              ? t('profile_page_hide_client_id')
                              : t('profile_page_show_client_id')) || ''
                          }
                          onClick={(): void => setShowClientId(!showClientId)}
                        >
                          {showClientId ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  value={clientSecret}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                    setClientSecret(event.target.value)
                  }
                  type={showClientSecret ? 'text' : 'password'}
                  size="small"
                  label={t('profile_page_client_secret')}
                  fullWidth
                  disabled={isGenerating}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={
                            (showClientSecret
                              ? t('profile_page_hide_client_secret')
                              : t('profile_page_show_client_secret')) || ''
                          }
                          onClick={(): void => setShowClientSecret(!showClientSecret)}
                        >
                          {showClientSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              {token && (
                <TextField
                  value={token}
                  size="small"
                  label={t('profile_page_api_token_label')}
                  fullWidth
                  disabled={isGenerating}
                />
              )}

              <div className="flex flex-row items-center justify-end gap-2">
                {token && (
                  <Tooltip placement="top" title={t('profile_page_copy') || ''} arrow>
                    <IconButton onClick={copyToClipboard} disabled={isGenerating}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Button
                  onClick={generateToken}
                  disabled={isGenerating || !clientId || !clientSecret}
                  variant="contained"
                  startIcon={isGenerating ? <CircularProgress size={16} /> : undefined}
                >
                  {t('profile_page_generate')}
                </Button>
              </div>
            </div>
          </Paper>
        )}
      </div>
    </div>
  );
};

interface UserInfoProps {
  userInfo: UserDto | null;
}

export default UserInfo;
